import { activeOrganizationHeaders } from '@center-technologies/shared-api-clients';
import type { A2uiComponentEntry } from 'types/space';

export interface ComposerAgentTarget {
  kind: 'space' | 'composition' | 'context' | 'schema';
  id: string;
}

export type ComposerAgentScope = { kind: 'org' } | { kind: 'approach'; systemName: string };

export interface ComposerAgentChatEntry {
  role: 'user' | 'assistant';
  content: string;
}

export interface ComposerProposalMessage {
  type: 'updateComponents' | 'updateDataModel';
  surfaceId: string;
  components?: A2uiComponentEntry[];
  patch?: Record<string, unknown>;
}

export interface ComposerAgentProposal {
  messages: ComposerProposalMessage[];
  rationale: string;
}

export interface ComposerAgentQuestionOption {
  label: string;
  description?: string;
  recommended?: boolean;
}

export interface ComposerAgentQuestion {
  id: string;
  text: string;
  options: ComposerAgentQuestionOption[];
  allowOther?: boolean;
}

export interface ComposerAgentQuestionSet {
  questions: ComposerAgentQuestion[];
}

/** Mirrors `@center-platform/composer-tools`'s `CreateContextParams` — the `create_context` tool's proposal, shown to the user for explicit confirmation before anything is written (EAINT-844 Phase F6d/F6e). */
export interface ComposerAgentContextConfirmation {
  name: string;
  description?: string;
  applicability?: Record<string, unknown>;
  definition: Record<string, unknown>;
  useCaseTags?: string[];
}

/**
 * EAINT-895 Phase 5: the `typeEditProposal` SSE frame — a `schema` target's `propose_type_edit`
 * call. Same never-writes contract as {@link ComposerAgentContextConfirmation}'s `contextProposal`
 * variant: the host turns this straight into a ghost in the named section, with no separate
 * confirm-then-POST route (a kept ghost applies through the composer's own existing save path).
 */
export interface ComposerAgentTypeEditProposal {
  sectionId: string;
  fields: Record<string, unknown>;
  rationale: string;
}

/**
 * An ambient, advisory-only observation the agent makes about the draft as it changes
 * (`/standards/agentic/agentic-ux-standards.md` — the critique cards and "Watching as you edit"
 * are Allow: read the draft and comment, never write). `block` names the contract block the
 * observation concerns (e.g. `'compose'`); acting on one goes through the existing ghost flow
 * ({@link ComposerAgentTypeEditProposal}) and its own Ask, never through this type directly.
 */
export interface ComposerTypeCritique {
  id: string;
  block: string;
  severity: 'info' | 'warn' | 'error';
  text: string;
}

/**
 * Where a ghost came from (`/standards/agentic/agentic-ux-standards.md` Rule 7 — a proposal a user
 * is asked to keep must say what produced it). Observed by the host that runs the turn, never sent
 * over the wire: the agent does not get to assert its own provenance, so the thread stamps the turn
 * it was answering and the moment the frame arrived.
 */
export interface ComposerGhostProvenance {
  /** Id of the thread turn (the user message) whose answer produced this proposal. */
  turnId: string;
  /** The user's own words for that turn — what the agent was asked. */
  prompt: string;
  /** ISO-8601 instant the proposal frame arrived. */
  at: string;
}

export interface SendComposerAgentMessageOptions {
  endpoint: string;
  message: string;
  conversationHistory: ComposerAgentChatEntry[];
  conversationId?: string;
  target: ComposerAgentTarget;
  scope?: ComposerAgentScope;
  draftDigest?: string;
  getToken?: () => Promise<string | undefined>;
  signal?: AbortSignal;
  onChatText: (text: string) => void;
  onProgress: (tool: string, status: 'calling' | 'done') => void;
  onProposal: (proposal: ComposerAgentProposal) => void;
  /** A `question` SSE frame (EAINT-844 Phase F6b `ask_structured_questions`) — the agent asking a clarifying question via selectable options instead of prose. */
  onQuestion: (questionSet: ComposerAgentQuestionSet) => void;
  /** A `contextConfirmation` SSE frame (EAINT-844 Phase F6d `create_context`) — the agent proposing a new context; never written until the user explicitly confirms via `confirmComposerContext`. */
  onConfirmContext: (confirmation: ComposerAgentContextConfirmation) => void;
  /**
   * A `contextProposal` SSE frame (EAINT-862 Phase 3 `create_context`, `context` target only) —
   * the agent proposing a walk for the context being built. Unlike {@link onConfirmContext}, there
   * is no separate write route: the caller shows it as a ghost overlay the user accepts (replacing
   * the in-progress draft) or dismisses directly in the editor.
   */
  onContextProposal: (proposal: ComposerAgentContextConfirmation) => void;
  /** A `typeEditProposal` SSE frame (EAINT-895 Phase 5 `propose_type_edit`, `schema` target only) — the agent proposing a ghost in one of the composer's sections. */
  onTypeEditProposal: (proposal: ComposerAgentTypeEditProposal) => void;
  /** Receives a STABLE error CODE from the service (e.g. `agent_failed`), not display text — the caller translates it. */
  onError: (code: string) => void;
  onDone: () => void;
}

/** Thrown for connection failures (fetch rejects, non-OK status) — distinct from an in-band `error` frame, so the caller can show an "unavailable" state instead of an inline chat error. */
export class ComposerAgentNetworkError extends Error {}

/**
 * Thrown when composer-agent-service answered but with a non-2xx status. Distinct from
 * {@link ComposerAgentNetworkError}, which means the service is unreachable and drives the whole
 * thread into its "unavailable" empty state — a 409 (name taken) or 403 (missing authority) is an
 * actionable per-request outcome, not a dead service.
 */
export class ComposerAgentRequestError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ComposerAgentRequestError';
  }
}

/**
 * Flattens a proposal's `updateComponents` messages into one component list — the shape
 * `GhostProposal` (composerStore.ts) expects. `updateDataModel` messages are dropped: the ghost
 * buffer only models component proposals today.
 */
export function proposalToGhostComponents(proposal: ComposerAgentProposal): A2uiComponentEntry[] {
  return proposal.messages
    .filter((m) => m.type === 'updateComponents' && Array.isArray(m.components))
    .flatMap((m) => m.components ?? []);
}

/**
 * Lightweight reachability probe (`GET {endpoint}/health`) used to show the composer's agent
 * thread as unavailable proactively when the composer opens, rather than only after a failed send.
 */
export async function probeComposerAgentAvailability(endpoint: string, signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch(`${endpoint}/health`, { method: 'GET', signal });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * POSTs one turn to composer-agent-service's `/chat/stream` and dispatches each SSE frame to the
 * matching callback. Not `EventSource` — the request needs a POST body (message + scope/target),
 * so this hand-rolls the fetch+ReadableStream split (mirrors the a2ui-prototype's `sse-client.ts`,
 * adapted to composer-agent-service's frame vocabulary: `chatText`/`progress`/`a2uiProposal`/
 * `question`/`error`/`done` — no `sphereChanged`, the composer domain has no "center_on" tool).
 */
export async function sendComposerAgentMessage(opts: SendComposerAgentMessageOptions): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...activeOrganizationHeaders() };
  if (opts.getToken) {
    const token = await opts.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${opts.endpoint}/chat/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        message: opts.message,
        conversationHistory: opts.conversationHistory,
        conversationId: opts.conversationId,
        target: opts.target,
        scope: opts.scope,
        draftDigest: opts.draftDigest,
      }),
      signal: opts.signal,
    });
  } catch (err) {
    if (opts.signal?.aborted) return;
    throw new ComposerAgentNetworkError(err instanceof Error ? err.message : 'composer-agent-service unreachable');
  }

  if (!response.ok || !response.body) {
    throw new ComposerAgentNetworkError(`composer-agent-service responded ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line.startsWith('data:')) continue;
      const json = line.slice('data:'.length).trim();
      if (!json) continue;
      let frame: Record<string, unknown>;
      try {
        frame = JSON.parse(json);
      } catch {
        continue;
      }
      if (typeof frame.chatText === 'string') {
        opts.onChatText(frame.chatText);
      } else if (frame.progress && typeof frame.progress === 'object') {
        const progress = frame.progress as { tool: string; status: 'calling' | 'done' };
        opts.onProgress(progress.tool, progress.status);
      } else if (frame.a2uiProposal && typeof frame.a2uiProposal === 'object') {
        opts.onProposal(frame.a2uiProposal as ComposerAgentProposal);
      } else if (frame.question && typeof frame.question === 'object') {
        opts.onQuestion(frame.question as ComposerAgentQuestionSet);
      } else if (frame.contextConfirmation && typeof frame.contextConfirmation === 'object') {
        opts.onConfirmContext(frame.contextConfirmation as ComposerAgentContextConfirmation);
      } else if (frame.contextProposal && typeof frame.contextProposal === 'object') {
        opts.onContextProposal(frame.contextProposal as ComposerAgentContextConfirmation);
      } else if (frame.typeEditProposal && typeof frame.typeEditProposal === 'object') {
        opts.onTypeEditProposal(frame.typeEditProposal as ComposerAgentTypeEditProposal);
      } else if (typeof frame.error === 'string') {
        opts.onError(frame.error);
      } else if (frame.done) {
        opts.onDone();
      }
    }
  }
}

export interface ConfirmComposerContextOptions {
  endpoint: string;
  confirmation: ComposerAgentContextConfirmation;
  getToken?: () => Promise<string | undefined>;
  signal?: AbortSignal;
}

export interface ConfirmComposerContextResult {
  systemName: string;
}

/**
 * Calls composer-agent-service's `POST /context/confirm` — the ONLY path that ever writes a
 * `create_context` proposal (EAINT-844 Phase F6d/F6e). The tool call itself never writes; this
 * function only runs when the user clicks Confirm on the rendered `contextConfirmation` card.
 */
export async function confirmComposerContext(opts: ConfirmComposerContextOptions): Promise<ConfirmComposerContextResult> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...activeOrganizationHeaders() };
  if (opts.getToken) {
    const token = await opts.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${opts.endpoint}/context/confirm`, {
      method: 'POST',
      headers,
      body: JSON.stringify(opts.confirmation),
      signal: opts.signal,
    });
  } catch (err) {
    if (opts.signal?.aborted) throw err;
    throw new ComposerAgentNetworkError(err instanceof Error ? err.message : 'composer-agent-service unreachable');
  }

  if (!response.ok) {
    throw new ComposerAgentRequestError(response.status, `composer-agent-service responded ${response.status}`);
  }

  const body = (await response.json()) as { systemName?: string };
  // `createContext` returns `systemName ?? ''`, so an empty string is a reachable "success" —
  // treat it as a failure rather than rendering "Created context " with a blank name.
  if (!body?.systemName) {
    throw new ComposerAgentRequestError(response.status, 'composer-agent-service confirm response is missing systemName');
  }
  return { systemName: body.systemName };
}
